/**
 * @packageDocumentation
 * @module f1/abstract-data-type
 */

import { TIterable, TGenerator, t_key } from '../types';
import is_function from '../is/is_function';
import is_record from '../is/is_record';
import is_array from '../is/is_array';
export interface Keys {
  /**
   * Generate keys from an array or record
   *
   * @public
   * @function f1.keys
   * @param adt the array or record we will generate keys from
   * @example
   *
   * ```ts
   * f1.keys([16, 17, 18]) === ["0", "1", "2"]
   * f1.keys({ a: 1 }) === ["a"]
   * ```
   *
   */
  <K extends t_key>(
    iter:
      | TIterable<unknown>
      | TGenerator<unknown>
      | ArrayLike<unknown>
      | Record<K, unknown>
      | Map<K, unknown>
      | Set<unknown>
  ): K[];
}

/**
 * @see Keys
 */
export default <K extends t_key>(
  iter:
    | TIterable<unknown>
    | TGenerator<unknown>
    | ArrayLike<unknown>
    | Record<K, unknown>
    | Map<K, unknown>
    | Set<unknown>
): K[] => {
  iter = is_function(iter) ? iter() : iter;

  return iter == null
    ? []
    : is_array(iter) || is_record(iter)
    ? (Object.keys(iter) as K[])
    : typeof iter['keys'] === 'function'
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (Array.from((iter as any).keys()) as K[])
    : ([] as K[]);
};
